import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import {
  fetchReplayReport,
  getReplay,
  listReplays,
  triggerReplay,
} from '../../services/queryLogReplayApi';
import {
  QueryLogReplayState,
  ReplayRecord,
  ReplayReportPayload,
  ReplayScaleFactor,
} from '../../types/queryLogReplay';

const initialState: QueryLogReplayState = {
  latestReplay: null,
  report: null,
  reportForReplayId: null,
  isLoadingReplay: false,
  isLoadingReport: false,
  isTriggering: false,
  triggerError: null,
  loadError: null,
};

const queryLogReplaySlice = createSlice({
  name: 'queryLogReplay',
  initialState,
  reducers: {
    setLoadingReplay(state, action: PayloadAction<boolean>) {
      state.isLoadingReplay = action.payload;
    },
    setLoadingReport(state, action: PayloadAction<boolean>) {
      state.isLoadingReport = action.payload;
    },
    setTriggering(state, action: PayloadAction<boolean>) {
      state.isTriggering = action.payload;
    },
    setLatestReplay(state, action: PayloadAction<ReplayRecord | undefined>) {
      state.latestReplay = action.payload;
    },
    setReport(
      state,
      action: PayloadAction<{
        replayId: string;
        report: ReplayReportPayload | null;
      }>
    ) {
      state.report = action.payload.report;
      state.reportForReplayId = action.payload.replayId;
    },
    setTriggerError(state, action: PayloadAction<string | null>) {
      state.triggerError = action.payload;
    },
    setLoadError(state, action: PayloadAction<string | null>) {
      state.loadError = action.payload;
    },
    /** Wipe everything — called when the user switches project/run scope or unmounts the tab. */
    resetReplayState() {
      return initialState;
    },
  },
});

export const {
  setLoadingReplay,
  setLoadingReport,
  setTriggering,
  setLatestReplay,
  setReport,
  setTriggerError,
  setLoadError,
  resetReplayState,
} = queryLogReplaySlice.actions;

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------

/**
 * Fetch the latest replay for this (project, run). The backend appends history rows on every
 * trigger; the UI only renders the newest one (user explicitly chose one-replay-per-run UX).
 *
 * Pass `silent=true` for the polling loop so we don't toggle the loading spinner on every tick
 * and cause UI flicker — only the initial mount-fetch should show the spinner.
 */
export function fetchLatestReplay(
  projectId: number,
  runId: number,
  options: { silent?: boolean } = {}
) {
  return async () => {
    if (!projectId || !runId) return;

    if (!options.silent) {
      dispatch(setLoadingReplay(true));
    }
    dispatch(setLoadError(null));

    try {
      const response = await listReplays(projectId, runId);
      // Backend returns replays sorted newest-first by `created_at`. Pick index 0 or undefined.
      const latest = response.replays[0];
      dispatch(setLatestReplay(latest));
    } catch (err: any) {
      const message =
        err?.displayMessage || err?.message || 'Failed to load replay status.';
      dispatch(setLoadError(message));
    } finally {
      if (!options.silent) {
        dispatch(setLoadingReplay(false));
      }
    }
  };
}

/**
 * Kick off a new replay. On success we eagerly fetch the latest replay so the UI flips from
 * idle → pending without waiting for the next poll cycle.
 */
export function startReplay(
  projectId: number,
  runId: number,
  scaleFactor: ReplayScaleFactor
) {
  return async () => {
    if (!projectId || !runId) {
      dispatch(setTriggerError('No project run selected.'));
      return;
    }

    dispatch(setTriggering(true));
    dispatch(setTriggerError(null));

    try {
      await triggerReplay(projectId, runId, { scale_factor: scaleFactor });
      // The trigger response already has the freshly-created PENDING record, but re-fetching the
      // list is more robust against concurrent triggers from another browser tab.
      await dispatch(fetchLatestReplay(projectId, runId, { silent: true }) as any);
    } catch (err: any) {
      // 409 = active replay already exists for this run. 400 = invalid request, missing secret,
      // etc. We surface the server's `message` field which both responses include.
      const apiMessage = err?.response?.data?.message;
      const message =
        apiMessage ||
        err?.displayMessage ||
        err?.message ||
        'Failed to start replay.';
      dispatch(setTriggerError(message));
    } finally {
      dispatch(setTriggering(false));
    }
  };
}

/**
 * Fetch the replay detail (which includes the presigned S3 URL) and follow that URL to
 * download the full report JSON. We only need this on the COMPLETED state to render
 * top-contested-tables data that doesn't live in the `metrics` summary.
 */
export function fetchReplayReportDetail(
  projectId: number,
  runId: number,
  replayId: string
) {
  return async () => {
    if (!projectId || !runId || !replayId) return;

    dispatch(setLoadingReport(true));
    dispatch(setLoadError(null));

    try {
      const detail = await getReplay(projectId, runId, replayId);
      if (!detail.report_presigned_url) {
        // No URL yet — the replay is not COMPLETED, or the bucket lookup failed server-side.
        dispatch(setReport({ replayId, report: null }));
        return;
      }
      const report = await fetchReplayReport(detail.report_presigned_url);
      dispatch(setReport({ replayId, report }));
    } catch (err: any) {
      const message =
        err?.displayMessage ||
        err?.message ||
        'Failed to load replay report details.';
      dispatch(setLoadError(message));
    } finally {
      dispatch(setLoadingReport(false));
    }
  };
}

export default queryLogReplaySlice.reducer;
